"use client";

import Link from "next/link";
import type {
  AnchorHTMLAttributes,
  ButtonHTMLAttributes,
  ReactNode,
  Ref,
} from "react";
import { cn } from "@/lib/utils";

const variants = {
  accent: "bg-sky-400 text-white hover-hover:hover:not-disabled:bg-sky-300",
  danger: "bg-red-500 text-white hover-hover:hover:not-disabled:bg-red-600",
  discord:
    "bg-discord text-white shadow-none hover-hover:hover:not-disabled:bg-discord-hover",
  ghost: "bg-transparent hover-hover:hover:not-disabled:bg-gray-300",
  primary: "bg-gray-1200 text-gray-100",
  secondary:
    "bg-preview-bg text-gray-1200 shadow-custom hover-hover:hover:not-disabled:bg-gray-100",
  special:
    "text-white bg-linear-to-b from-sky-400 to-sky-500 shadow-[0px_0px_1px_1px_rgba(255,255,255,0.06)_inset,0px_1.5px_2px_0px_rgba(0,0,0,0.1),0px_0px_0px_1px_var(--color-sky-500)]",
  success:
    "bg-green-500 text-white hover-hover:hover:not-disabled:bg-green-600",
  tertiary:
    "bg-gray-300 text-gray-1100 hover-hover:hover:not-disabled:bg-gray-400",
};

const sizeClasses = {
  lg: "btn-lg",
  md: "btn-md",
  sm: "btn-sm",
  xl: "btn-xl",
  xs: "btn-xs",
};

type ButtonVariant = keyof typeof variants;
type ButtonSize = keyof typeof sizeClasses;

interface ButtonBaseProps {
  children?: ReactNode;
  className?: string;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  rounded?: boolean;
  size?: ButtonSize;
  static?: boolean;
  variant?: ButtonVariant;
}

type ButtonAsLinkProps = ButtonBaseProps &
  Omit<AnchorHTMLAttributes<HTMLAnchorElement>, keyof ButtonBaseProps> & {
    href: string;
    native?: boolean;
    prefetch?: boolean;
    ref?: Ref<HTMLAnchorElement>;
  };

type ButtonAsButtonProps = ButtonBaseProps &
  Omit<ButtonHTMLAttributes<HTMLButtonElement>, keyof ButtonBaseProps> & {
    href?: undefined;
    native?: undefined;
    prefetch?: undefined;
    ref?: Ref<HTMLButtonElement>;
  };

type ButtonProps = ButtonAsLinkProps | ButtonAsButtonProps;

function ButtonContents({
  children,
  leftIcon,
  rightIcon,
}: {
  children?: ReactNode;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
}) {
  const hasLabel =
    children !== undefined && children !== null && children !== false;
  return (
    <>
      {leftIcon}
      {hasLabel ? (
        <span className="contents select-none">{children}</span>
      ) : null}
      {rightIcon}
    </>
  );
}

function buildClassName({
  variant,
  size,
  rounded,
  isStatic,
  className,
}: {
  variant: ButtonVariant;
  size: ButtonSize;
  rounded: boolean;
  isStatic: boolean;
  className?: string;
}) {
  return cn(
    "btn",
    sizeClasses[size],
    variants[variant],
    !isStatic && "btn-tap",
    rounded && "btn-rounded",
    className
  );
}

function Button({
  variant = "secondary",
  size = "md",
  leftIcon,
  rightIcon,
  rounded = false,
  static: isStatic = false,
  className,
  children,
  ...props
}: ButtonProps) {
  const sharedClassName = buildClassName({
    className,
    isStatic,
    rounded,
    size,
    variant,
  });

  const content = (
    <ButtonContents leftIcon={leftIcon} rightIcon={rightIcon}>
      {children}
    </ButtonContents>
  );

  if (props.href === undefined) {
    return (
      <button className={sharedClassName} {...props}>
        {content}
      </button>
    );
  }

  const { native, prefetch, ...anchorProps } = props;

  if (native) {
    return (
      <a className={sharedClassName} {...anchorProps}>
        {content}
      </a>
    );
  }

  return (
    <Link className={sharedClassName} prefetch={prefetch} {...anchorProps}>
      {content}
    </Link>
  );
}

export type { ButtonProps, ButtonSize, ButtonVariant };
export { Button };
